import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import { debounce } from 'lodash-es'
import { ResizeObserver } from 'resize-observer'
import { css } from 'src/lib/emotion'
import { gsap, Power3 } from 'src/lib/gsap'

import { colors, spacing } from '../../tailwind'
import { rafDebounce } from '../../utils'
import { classes, hasPosition } from '../../utils/css'

function shadow(dir: string, color: string) {
  return `radial-gradient(ellipse at ${dir}, ${color}, transparent 75%)`
}

const hideScrollbar = css`
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  & {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

const disabled = css`
  &,
  * {
    pointer-events: none !important;
  }
`

@Component({
  tag: 'ui-scrollarea',
})
export class ScrollArea {
  @Prop() shadowColor = colors['surface-lower']
  @Prop({ mutable: true }) scrollTarget: any
  @Prop() scrollOffsetX = parseInt(spacing[4], 10)
  @Prop() scrollOffsetY = parseInt(spacing[4], 10)
  @Prop() overflowX = true
  @Prop() overflowY = true
  @Prop() shadowStyleX: Record<string, string> = {}
  @Prop() shadowStyleY: Record<string, string> = {}
  @Prop() allowDragging = false
  @Prop() disabled = false
  @Prop() showBarX = false
  @Prop() showBarY = false

  @Prop() disableObservers = false

  @Element() el: HTMLUiScrollareaElement

  @Event() scrollStart: EventEmitter
  @Event() scrollEnd: EventEmitter
  @Event() scrolled: EventEmitter<{ top: number; left: number; pctX: number; pctY: number }>
  @Event() userScrolled: EventEmitter<number>

  @State() canScroll: { [dir in 'up' | 'down' | 'left' | 'right' | 'x' | 'y']?: boolean } = {}

  @State() scrollAmountX = 1
  @State() scrollAmountY = 1
  @State() scrollPctX = 0
  @State() scrollPctY = 0
  @State() active = false
  @State() pressed = false

  dragging: boolean

  @Listen('wheel', { passive: true })
  onWheel() {
    const distance = this.scrollingEl.scrollHeight - this.scrollingEl.clientHeight
    const pct = this.scrollingEl.scrollTop / distance
    if (!isNaN(pct) && distance > 0) {
      this.userScrolled.emit(pct)
    }
  }

  _sentinelTop: HTMLDivElement
  get sentinelTop() {
    return this._sentinelTop
  }
  set sentinelTop(value: HTMLDivElement) {
    if (this._sentinelTop) {
      this.observer?.unobserve(this._sentinelTop)
    }
    if (value) {
      this.observer?.observe(value)
    }
    this._sentinelTop = value
  }

  _sentinelLeft: HTMLDivElement
  get sentinelLeft() {
    return this._sentinelLeft
  }
  set sentinelLeft(value: HTMLDivElement) {
    if (this._sentinelLeft) {
      this.observer?.unobserve(this._sentinelLeft)
    }
    if (value) {
      this.observer?.observe(value)
    }
    this._sentinelLeft = value
  }

  _sentinelRight: HTMLDivElement
  get sentinelRight() {
    return this._sentinelRight
  }
  set sentinelRight(value: HTMLDivElement) {
    if (this._sentinelRight) {
      this.observer?.unobserve(this._sentinelRight)
    }
    if (value) {
      this.observer?.observe(value)
    }
    this._sentinelRight = value
  }

  _sentinelBtm: HTMLDivElement
  get sentinelBtm() {
    return this._sentinelBtm
  }
  set sentinelBtm(value: HTMLDivElement) {
    if (this._sentinelBtm) {
      this.observer?.unobserve(this._sentinelBtm)
    }
    if (value) {
      this.observer?.observe(value)
    }
    this._sentinelBtm = value
  }

  shadowTop: HTMLDivElement
  shadowBtm: HTMLDivElement
  shadowLeft: HTMLDivElement
  shadowRight: HTMLDivElement
  shadowContainer: HTMLDivElement

  observer: IntersectionObserver
  resizeObserver: ResizeObserver

  scrollingEl: HTMLElement
  hasPosition: boolean

  _contentEl: HTMLDivElement

  get contentEl() {
    return this._contentEl
  }

  set contentEl(value: HTMLDivElement) {
    if (this._contentEl) {
      this.resizeObserver?.unobserve(this._contentEl)
    }
    if (value) {
      this.resizeObserver?.observe(value)
    }

    this._contentEl = value
  }

  scrollWidth: number
  scrollHeight: number
  contentWidth: number
  contentHeight: number

  /**
   * Determines which shadows to show based on position of "sentinel" elements,
   * if they are intersecting it means that the container has reached the end of
   * that particular scrolling direction
   */
  checkScrolling = (entries: IntersectionObserverEntry[]) => {
    if (!this.sentinelTop || !this.sentinelBtm || !this.sentinelLeft || !this._sentinelRight) {
      return
    }
    const canScroll: this['canScroll'] = {}

    entries.map(({ isIntersecting, target }) => {
      switch (target) {
        case this.sentinelTop:
          return (canScroll.up = !isIntersecting)
        case this.sentinelBtm:
          return (canScroll.down = !isIntersecting)
        case this.sentinelLeft:
          return (canScroll.left = !isIntersecting)
        case this.sentinelRight:
          return (canScroll.right = !isIntersecting)
      }
    })

    canScroll.x = canScroll.left || canScroll.right
    canScroll.y = canScroll.up || canScroll.down

    this.canScroll = { ...this.canScroll, ...canScroll }
  }

  checkContent = () => {
    if (!this.scrollingEl) {
      return
    }
    const { scrollWidth, scrollHeight, clientWidth, clientHeight } = this.scrollingEl
    this.scrollWidth = scrollWidth
    this.scrollHeight = scrollHeight
    this.contentWidth = clientWidth
    this.contentHeight = clientHeight
    this.scrollAmountX = clientWidth / scrollWidth
    this.scrollAmountY = clientHeight / scrollHeight
  }

  onScroll() {
    const top = this.scrollingEl.scrollTop
    const left = this.scrollingEl.scrollLeft
    this.active = true
    this.scrollPctX = this.scrollingEl.scrollLeft / (this.scrollWidth - this.contentWidth)
    this.scrollPctY = this.scrollingEl.scrollTop / (this.scrollHeight - this.contentHeight)
    this.scrolled.emit({ top, left, pctX: this.scrollPctX, pctY: this.scrollPctY })
    this.endScroll()
  }

  endScroll = debounce(() => {
    this.active = false
    this.scrollEnd.emit()
  }, 700)

  /** Scrolls to an element reference or css selector string */

  @Watch('scrollTarget')
  onScrollTarget() {
    this.scrollToElement(this.scrollTarget)
  }

  @Method()
  async scrollToElement(el: HTMLElement, vars?: GSAPTweenVars) {
    if (el) {
      this.active = true
      this.scrollStart.emit()
      return new Promise<void>((resolve) => {
        gsap.to(this.scrollingEl, {
          scrollTo: {
            y: el,
            x: el,
            offsetX: this.scrollOffsetX,
            offsetY: this.scrollOffsetY,
            autoKill: true,
            onAutoKill: this.endScroll,
          },
          ease: Power3.easeInOut,
          onComplete: () => {
            requestAnimationFrame(() => {
              resolve()
              this.endScroll()
            })
          },
          ...vars,
        })
      })
    }
  }

  @Method()
  async scrollCustom(scrollTo?: ScrollToPlugin.Vars) {
    this.scrollStart.emit()
    this.active = true
    return new Promise<void>((resolve) => {
      gsap.to(this.scrollingEl, {
        scrollTo: { autoKill: true, ...scrollTo },
        ease: Power3.easeInOut,
        onComplete: () => {
          resolve()
          this.endScroll()
        },
      })
    })
  }

  @Method()
  async scrollToPct(pct: number) {
    const y = (this.scrollingEl.scrollHeight - this.scrollingEl.clientHeight) * pct
    this.active = true
    gsap.to(this.scrollingEl, {
      scrollTo: { autoKill: true, y },
      ease: 'none',
      onComplete: this.endScroll,
    })
  }

  componentWillLoad() {
    if (!this.disableObservers) {
      this.observer = new IntersectionObserver(this.checkScrolling, { root: this.el })
      this.resizeObserver = new ResizeObserver(this.checkContent)
    }
    this.hasPosition = hasPosition(this.el)
  }

  componentDidLoad() {
    if (this.scrollTarget) {
      this.onScrollTarget()
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect()
    this.resizeObserver?.disconnect()
    this.endScroll?.cancel()
  }

  @Listen('mouseup', { target: 'window' })
  onMouseUpOutside() {
    this.pressed = false
    this.dragging = false
    this.el.removeEventListener('mousemove', this.dragScroll)
  }

  dragEvent: MouseEvent
  dragScrollAmount = 0
  dragInterval: number

  dragScroll = rafDebounce((e: MouseEvent) => {
    const rect = this.scrollingEl.getBoundingClientRect()
    const limit = 70
    const maxScrollPerFrame = 20
    if (this.dragEvent.clientY > e.clientY) {
      // Scroll up
      const distance = Math.min(e.clientY - rect.top, limit)
      const speed = 1 - distance / limit
      const amount = -maxScrollPerFrame * speed
      gsap.to(this, { duration: 0.2, dragScrollAmount: amount, ease: 'none' })
    } else if (this.dragEvent.clientY < e.clientY) {
      // Scroll down
      const distance = Math.min(rect.bottom - e.clientY, limit)
      const speed = 1 - distance / limit
      const amount = maxScrollPerFrame * speed
      gsap.to(this, { duration: 0.2, dragScrollAmount: amount, ease: 'none' })
    }
    this.dragEvent = e
  })

  applyDragScroll = () => {
    window.cancelAnimationFrame(this.dragInterval)
    if (!this.dragging) {
      return
    }
    if (this.dragScrollAmount !== 0) {
      this.scrollingEl.scrollBy({ top: this.dragScrollAmount })
    }
    this.dragInterval = requestAnimationFrame(this.applyDragScroll)
  }

  render() {
    return (
      <Host
        class={`
          overflow-hidden flex pointer-events-none
          ${classes(!this.hasPosition, 'relative')}
          ${classes(this.disabled, disabled)}
          ${classes(this.showBarX && this.canScroll.x, 'pb-3')}
          ${classes(this.showBarY && this.canScroll.y, 'pr-3')}
        `}
        onMouseEnter={() => {
          this.active = true
          this.endScroll()
        }}
        onMouseLeave={() => (this.active = false)}
        onMouseDown={(e) => {
          if (!this.allowDragging) {
            return
          }
          this.dragEvent = e
          this.dragging = true
          this.applyDragScroll()
          this.el.addEventListener('mousemove', this.dragScroll)
        }}
        onScroll={() => {
          // Fix a weird bug where the element itself gets scrolled when
          // something is clicked and throws things off
          this.el.scrollTop = 0
        }}
      >
        <section
          ref={(el) => (this.scrollingEl = el)}
          onScroll={(event) => {
            event.stopImmediatePropagation()
            this.onScroll()
          }}
          draggable={false}
          class={`flex flex-col w-full max-h-full overflow-auto ${hideScrollbar} pointer-events-auto`}
        >
          <div class='flex-shrink-0 h-1px' ref={(el) => (this.sentinelTop = el)} />
          <div class='flex flex-1'>
            <div class='flex-shrink-0 w-1px' ref={(el) => (this.sentinelLeft = el)} />
            <div
              class={`
                flex-auto
                ${classes(!this.overflowX, 'overflow-x-hidden')}
                ${classes(!this.overflowY, 'overflow-y-hidden')}
              `}
              ref={(el) => (this.contentEl = el)}
            >
              <slot />
            </div>
            <div class='flex-shrink-0 w-1px' ref={(el) => (this.sentinelRight = el)} />
          </div>
          <div class='flex-shrink-0 h-1px' ref={(el) => (this.sentinelBtm = el)} />
        </section>
        <div
          ref={(el) => (this.shadowContainer = el)}
          class='overflow-hidden absolute top-0 left-0 w-full h-full pointer-events-none'
        >
          <div
            class={`
              absolute top-0 left-0 w-full h-8 transition-opacity duration-200
              ${this.canScroll.up ? 'opacity-100' : 'opacity-0'}
            `}
            style={{
              boxShadow: '0 1px 0 rgba(0,0,0,0.2) inset',
              backgroundImage: shadow('top', this.shadowColor),
              ...this.shadowStyleY,
            }}
          />
          <div
            class={`
              absolute bottom-0 left-0 w-full h-8 transition-opacity duration-200
              ${this.canScroll.down ? 'opacity-100' : 'opacity-0'}
            `}
            style={{
              boxShadow: '0 -1px 0 rgba(0,0,0,0.2) inset',
              backgroundImage: shadow('bottom', this.shadowColor),
              ...this.shadowStyleY,
            }}
          />
          {this.overflowX && (
            <Fragment>
              <div
                class={`
                  absolute top-0 left-0 w-8 h-full transition-opacity duration-200
                  ${this.canScroll.left ? 'opacity-100' : 'opacity-0'}
                `}
                style={{ backgroundImage: shadow('left', this.shadowColor), ...this.shadowStyleX }}
              />
              <div
                class={`
                  absolute top-0 right-0 w-8 h-full transition-opacity duration-200
                  ${this.canScroll.right ? 'opacity-100' : 'opacity-0'}
                `}
                style={{ backgroundImage: shadow('right', this.shadowColor), ...this.shadowStyleX }}
              />
            </Fragment>
          )}
        </div>
        <div
          class={`
            absolute top-0 bottom-0 left-0 right-0 transition-opacity duration-200 group pointer-events-none
          `}
        >
          <ui-scrollbar
            axis='y'
            class={`
              absolute top-0 right-0 bottom-0 my-1 pointer-events-auto group-hover:opacity-100
              ${classes(!this.showBarY && !this.active && !this.pressed, 'opacity-0')}
            `}
            contentPct={this.scrollAmountY}
            scrollPct={this.scrollPctY}
            onMouseDown={() => (this.pressed = true)}
            onMouseUp={(e: MouseEvent) => {
              if (this.pressed) {
                e.stopImmediatePropagation()
                this.pressed = false
              }
            }}
            onUpdated={(e) => {
              this.userScrolled.emit(e.detail)
              gsap.killTweensOf(this.scrollingEl)
              this.scrollingEl.scrollTo({ top: (this.scrollHeight - this.contentHeight) * e.detail })
            }}
          />
          <ui-scrollbar
            axis='x'
            class={`
              absolute right-0 bottom-0 left-0 mx-1 pointer-events-auto group-hover:opacity-100
              ${classes(!this.showBarX && !this.active && !this.pressed, 'opacity-0')}
            `}
            contentPct={this.scrollAmountX}
            scrollPct={this.scrollPctX}
            onMouseDown={() => (this.pressed = true)}
            onMouseUp={(e: MouseEvent) => {
              if (this.pressed) {
                e.stopImmediatePropagation()
                this.pressed = false
              }
            }}
            onUpdated={(e) => {
              this.userScrolled.emit(e.detail)
              gsap.killTweensOf(this.scrollingEl)
              this.scrollingEl.scrollTo({ left: (this.scrollWidth - this.contentWidth) * e.detail })
            }}
          />
        </div>
      </Host>
    )
  }
}
